import Link from 'next/link'
import * as React from 'react'

import { TEMPLATE_DEFAULT_URLS } from '@/configs/template-default-urls'

interface NonFsdsLpHeroSectionProps {
  title: string
  subtitle: string
  badge: string
  ctaEnroll: string
  ctaCurriculum: string
  ctaEnrollUrl?: string
  ctaCurriculumUrl?: string
  highlights: Array<{ value: string; label: string }>
}

export default function NonFsdsLpHeroSection({
  title,
  subtitle,
  badge,
  ctaEnroll,
  ctaCurriculum,
  ctaEnrollUrl = TEMPLATE_DEFAULT_URLS.myCourses,
  ctaCurriculumUrl = '#course-path',
  highlights,
}: NonFsdsLpHeroSectionProps) {
  return (
    <section
      data-testid="non-fsds-lp-hero-section"
      className="relative overflow-hidden bg-gradient-to-b from-blue-50 via-sky-50/80 to-white px-4 pb-16 pt-20 sm:px-6 sm:pb-20 sm:pt-24 lg:px-8 lg:pb-24 lg:pt-28"
    >
      {/* Decorative radial accents */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(59,130,246,0.15),transparent)]" />
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-200 to-transparent" />

      <div className="relative mx-auto max-w-4xl text-center">
        {/* Badge */}
        <p className="mb-5 inline-flex items-center rounded-full border border-blue-200 bg-white/80 px-4 py-1.5 text-sm font-semibold text-blue-700 shadow-sm backdrop-blur-sm">
          <span className="mr-2 inline-block size-2 rounded-full bg-blue-500" />
          {badge}
        </p>

        <h1 className="mx-auto max-w-3xl text-3xl font-bold leading-tight tracking-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-[3.25rem] lg:leading-[1.15]">
          {title}
        </h1>

        <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">{subtitle}</p>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link
            href={ctaEnrollUrl}
            className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-blue-600 px-8 py-3 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all duration-200 hover:bg-blue-700 hover:shadow-blue-600/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:w-auto"
          >
            {ctaEnroll}
          </Link>
          <Link
            href={ctaCurriculumUrl}
            className="inline-flex min-h-12 w-full items-center justify-center rounded-full border border-blue-200 bg-white px-8 py-3 text-base font-semibold text-blue-700 transition-all duration-200 hover:bg-blue-50 hover:border-blue-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 sm:w-auto"
          >
            {ctaCurriculum}
            <svg className="ml-2 size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </Link>
        </div>

        <div className="mx-auto mt-10 flex max-w-xl flex-wrap items-center justify-center gap-3 sm:gap-4">
          {highlights.map(item => (
            <div
              key={item.label}
              className="flex items-center gap-2 rounded-2xl border border-blue-100/80 bg-white/90 px-4 py-2.5 shadow-sm backdrop-blur-sm"
            >
              <span className="text-lg font-bold text-blue-700 sm:text-xl">{item.value}</span>
              <span className="text-sm font-medium text-slate-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
